import { Box, Business, Button, Text } from '@doda/io-react/common';
import { PageTitle } from '@doda/io-react/pc';
import type {
  JobDetailResponse,
  ScoutJobDetailResponse,
} from '@doda/api-client/aspida/@types';
import { JOB_MANUSCRIPT_TYPE } from '@doda/common/src/constants/jobManuscriptType';
import { TAB_STATE } from '@doda/common/src/constants/jobSearchDetailTab';
import { useAppDispatch, useAppSelector } from 'redux/hooks';
import { matchedTagsSelector } from '@doda/common/src/redux/features/matchedTags';
import { getJobOfferKindIsAgent } from '@doda/common/src/logics/jobSearchDetail/getJobOfferKind';
import { makeCorporateFullName } from '@doda/common/src/logics/jobSearchDetail/makeCorporateFullName';
import { matchedTagCassetteAbTestGroupNoSelector } from '@doda/common/src/redux/features/abTest';
import { MatchedTagCassetteABTestPattern } from '@doda/common/src/constants/matchedTagCassette/matchedTagCassette';
import { useMatchedTagCassetteAnalytics } from '@doda/common/src/hooks/useMatchedTagCassetteAnalytics';
import { MatchedTagCassette } from '../MatchedTagCassette';
import type { Tab } from '../../types/tab';
import { ApplyMessage } from './ApplyMessage';
import { TagGroup, type TagGroupProps } from '../TagGroup';
import { MessageLink } from '../MessageLink';
import { MatchingBalloon } from './MatchingBalloon';
import { measureClickCompanyPageLink } from './measureClickCompanyPageLink';
import styles from './index.module.scss';

/** 見出しprops */
interface HeadingProps {
  corporateName: string;
  occupationName: string;
  tagList: TagGroupProps['tagList'];
  isScout: TagGroupProps['isScout'];
  publishingSchedulePeriodStartDate: string;
  publishingSchedulePeriodEndDate: string;
  publishingPeriodDate: string;
  scoutMessage: ScoutJobDetailResponse['scoutMessage'];
  hasCompanyPageLink: boolean;
  companyId: string;
  wcnsId: string;
  jid: string;
  hasArticleContent: boolean;
  welcomeMatch: JobDetailResponse['welcomeMatch'];
  isBookmarked: boolean;
  hasInterested: boolean;
  apply: JobDetailResponse['apply'];
  jobOfferKind: string;
  initialTab: Tab;
}

/** 見出し */
export const Heading = ({
  corporateName,
  occupationName,
  tagList,
  isScout,
  publishingSchedulePeriodStartDate,
  publishingSchedulePeriodEndDate,
  publishingPeriodDate,
  scoutMessage,
  hasCompanyPageLink,
  companyId,
  wcnsId,
  jid,
  hasArticleContent,
  welcomeMatch,
  hasInterested,
  isBookmarked,
  apply,
  jobOfferKind,
  initialTab,
}: HeadingProps) => {
  const dispatch = useAppDispatch();
  const showMessageLink = Boolean(scoutMessage?.url);
  const showCompanyPageLink = wcnsId === 'T' && hasCompanyPageLink;
  const showMatchingBalloon =
    (welcomeMatch && welcomeMatch.type) || (!isBookmarked && hasInterested);
  const isAgent = getJobOfferKindIsAgent(jobOfferKind);
  const matchedTags = useAppSelector(matchedTagsSelector);
  const skillMatchingAbTestGroup = useAppSelector(
    matchedTagCassetteAbTestGroupNoSelector
  );
  const isShowMatchedTagCassette =
    skillMatchingAbTestGroup === MatchedTagCassetteABTestPattern.TEST1 &&
    matchedTags &&
    matchedTags.length > 0;
  useMatchedTagCassetteAnalytics(skillMatchingAbTestGroup, matchedTags || []);

  return (
    <Box boxShadow="blue" className={styles['jobSearchDetail-heading']}>
      <div className={styles['jobSearchDetail-heading__innerBlock']}>
        <div className={styles['jobSearchDetail-heading__publishingDate']}>
          <Text color="gray" size="10" weight="normal" as="span">
            {`掲載予定期間：${publishingSchedulePeriodStartDate}～${publishingSchedulePeriodEndDate}`}
          </Text>
          <Text
            color="gray"
            size="10"
            weight="normal"
            as="span"
            style={{ marginInlineStart: 12 }}
          >
            {publishingPeriodDate && `更新日：${publishingPeriodDate}`}
          </Text>
        </div>
        {/* 応募済みメッセージ */}
        {apply?.isApplied && (
          <div className={styles['jobSearchDetail-heading__applyMessage']}>
            <ApplyMessage apply={apply} isAgent={isAgent} />
          </div>
        )}
        {/* マッチングバルーン */}
        {showMatchingBalloon && (
          <div className={styles['jobSearchDetail-heading__matchingBalloon']}>
            <MatchingBalloon
              welcomeMatch={welcomeMatch}
              hasInterested={hasInterested}
              wcnsId={wcnsId}
            />
          </div>
        )}
        {/* メッセージリンク */}
        {showMessageLink && (
          <div className={styles['jobSearchDetail-heading__messageLink']}>
            <MessageLink scoutMessage={scoutMessage} position="Header" />
          </div>
        )}
        <hgroup>
          {wcnsId === JOB_MANUSCRIPT_TYPE.typeE &&
          initialTab === TAB_STATE.pr ? (
            <Text
              color="black_pc"
              size="28"
              weight="bold"
              className={styles['jobSearchDetail-heading__title']}
            >
              {corporateName}
            </Text>
          ) : (
            <PageTitle className={styles['jobSearchDetail-heading__title']}>
              {corporateName}
            </PageTitle>
          )}
          {/* 企業情報リンク */}
          {showCompanyPageLink && (
            <p>
              <Button
                variant="outline_gray"
                size="S"
                as="a"
                href={`/DodaFront/View/Company/j_id__${companyId}/`}
                target="_blank"
                onClick={(event) =>
                  dispatch(
                    measureClickCompanyPageLink(event, {
                      jid,
                      hasArticleContent,
                    })
                  )
                }
                className={styles['jobSearchDetail-heading__companyPageLink']}
              >
                <Business color="blue" size="M" />
                <Text
                  color="black_pc"
                  size="12"
                  weight="bold"
                  as="span"
                  style={{ marginInlineStart: 4 }}
                >
                  企業情報を詳しく見る
                </Text>
              </Button>
            </p>
          )}
          <Text
            color="black_pc"
            size="14--articlePC"
            weight="normal"
            as="p"
            style={{ marginBlock: '2px 8px' }}
          >
            {occupationName}
          </Text>
        </hgroup>
        <TagGroup tagList={tagList} isScout={isScout} />
        {isShowMatchedTagCassette && (
          <MatchedTagCassette matchedTagList={matchedTags} />
        )}
      </div>
    </Box>
  );
};

/**
 * 見出しprops生成
 */
export function getHeadingProps(
  job: JobDetailResponse | ScoutJobDetailResponse,
  isScout: boolean,
  initialTab: Tab
): HeadingProps {
  const jobDetail = job.job || {};
  return {
    corporateName: makeCorporateFullName(jobDetail),
    occupationName: jobDetail.occupationName || '',
    tagList: jobDetail.kodawariIconList || [],
    isScout,
    publishingSchedulePeriodStartDate:
      jobDetail.publishingSchedulePeriodStartDate || '',
    publishingSchedulePeriodEndDate:
      jobDetail.publishingSchedulePeriodEndDate || '',
    publishingPeriodDate: jobDetail.publishingPeriodDate || '',
    scoutMessage: 'scoutMessage' in job ? job.scoutMessage || {} : undefined,
    hasCompanyPageLink: Boolean(jobDetail.hasCompanyPageLink),
    companyId: jobDetail.companyId || '',
    wcnsId: jobDetail.wcnsId || '',
    jid: jobDetail.jid || '',
    hasArticleContent: Boolean(jobDetail.hasArticleContent),
    welcomeMatch: job.welcomeMatch,
    hasInterested: Boolean(job.hasInterested),
    isBookmarked: Boolean(job.isBookmarkSaved),
    apply: job.apply,
    jobOfferKind: jobDetail.jobOfferKind || '',
    initialTab,
  };
}
